import { NoSsr } from "@mui/base";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { checkIsValidEmail, useSignUpNewsletter } from "scmp-sdk";

import { accountAtom } from "shared/lib/account";

import { Button } from "scmp-app/components/posties/button";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import {
  Container,
  DottySpan,
  EmailInput,
  GoogleTerms,
  StyledIconAlertCircle,
  TermsAndConditions,
  WarningMessage,
  Wrapper,
} from "./styles";

type Props = {
  className?: string;
  onSuccess?: () => void;
};

export const NewsletterWidget: FunctionComponent<Props> = ({ className, onSuccess }) => {
  const [emailInput, setEmailInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { handleSubmit: loggedInUserHandleSubmit, handleSubmitWithEmail } = useSignUpNewsletter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { isLoggedIn, user } = useAtomValue(accountAtom);
  const isEmailExist = isLoggedIn && user?.useremail;
  const buttonText = isLoggedIn ? "SIGN UP" : "JOIN US";

  const trackPostiesNewsletterSignup = () => {
    sendGA4Tracking({
      action: "click",
      category: "nl",
      customized_parameters: {
        method: "email",
        newsletter_name: "SCMP Parents",
        trigger_point: "posties",
      },
      subcategory: "sign_up",
    });
  };

  const handleSubmit = async () => {
    if (!checkIsValidEmail(emailInput)) {
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setIsError(false);
    const recaptchaToken = await executeRecaptcha?.();
    if (!recaptchaToken) return;
    await handleSubmitWithEmail(emailInput, recaptchaToken, true);
    onSuccess?.();
    setIsLoading(false);
  };

  const handleJoinUsButtonClick = () => {
    if (isLoading) {
      return;
    }

    void handleSubmit();
  };

  const handleSignUpButtonClick = async () => {
    if (!user?.useremail) return;

    setIsLoading(true);
    const { error } = await loggedInUserHandleSubmit(user.useremail);
    if (error) {
      setIsError(true);
      return;
    } else {
      onSuccess?.();
      setIsLoading(false);
    }
  };

  return (
    <Container
      className={className}
      method="post"
      onSubmit={event => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <NoSsr>
        {!isEmailExist && (
          <>
            <Wrapper>
              <EmailInput
                $isError={isError}
                onChange={event => {
                  setEmailInput(event.target.value);
                }}
                placeholder="Enter your email"
                value={emailInput}
              />
              {isError && (
                <WarningMessage>
                  <StyledIconAlertCircle />
                  Please enter a valid email and try again
                </WarningMessage>
              )}
              <TermsAndConditions>
                By submitting, you consent to receiving marketing emails from SCMP.
              </TermsAndConditions>
            </Wrapper>
            <GoogleTerms>
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </GoogleTerms>
          </>
        )}
        <Button
          onClick={event => {
            event.preventDefault();
            trackPostiesNewsletterSignup();
            return isEmailExist ? handleSignUpButtonClick() : handleJoinUsButtonClick();
          }}
          variant="button"
        >
          {isLoading ? <DottySpan>SENDING</DottySpan> : buttonText}
        </Button>
      </NoSsr>
    </Container>
  );
};

NewsletterWidget.displayName = "NewsletterWidget";
